// @flow
// $FlowFixMe
import React, { useMemo, Fragment } from "react";
import { noop, get, isEmpty } from "lodash";
import type {
  RunnerInformation,
  RunnerStats,
  PastRacesResults,
  HelpTypeEnum,
  SimpleBettingInterest
} from "@fdr/types/ProgramPage";
import { type RaceCardBettingInterest } from "@tvg/types/Race";
import { ApolloQueryResult } from "@apollo/client";
import type { NullaryFn, BinaryFn } from "@tvg/types/Functional";
import RunnerInfoMask from "@fdr/static-ui/Masks/runnerInfo";
import RunnerInfoMaskV2 from "@fdr/static-ui/Masks/runnerInfoV2";
import Tag from "../../_atoms/Tag";
import OddsDescription from "../../_molecules/OddsDescription";
import RunnerRating from "../../_molecules/RunnerRating";
import PastPerformances from "../../_molecules/PastPerformances";
import {
  RunnerInfoContainer,
  RunnerRatingTagContainer,
  PastRacesError,
  RetryButton
} from "./styled-components";

type Props = {
  runner: ?RunnerInformation,
  runnerStats: ?RunnerStats,
  pastRaces: ?Array<?PastRacesResults>,
  numberOfRaces: number,
  isLoadingPP: boolean,
  isLeg: boolean,
  openHelpModal: BinaryFn<HelpTypeEnum, boolean, void>,
  showIPP: boolean,
  openPastPerformances: NullaryFn<void>,
  refetch: NullaryFn<ApolloQueryResult>,
  pastPerformanceError: string,
  useOldPercentageRating: boolean,
  useGraphStatusV2?: boolean,
  shouldShowFirstOnboarding: boolean,
  isAllRunnersOpen: boolean,
  onTooltipClick: NullaryFn<void>,
  setOnboardingStatus: BinaryFn<string, string, void>,
  numColumns: number,
  isNumberFireSection: boolean,
  bettingInterest: RaceCardBettingInterest | ?SimpleBettingInterest,
  showRunnerUpdate: boolean,
  isWagerable: boolean,
  showWinProb: boolean,
  showPastRaces?: boolean
};

const RunnerInfo = (props: Props) => {
  const {
    runner,
    runnerStats,
    pastRaces,
    numberOfRaces,
    isLoadingPP,
    isLeg,
    openHelpModal,
    showIPP,
    openPastPerformances,
    refetch,
    pastPerformanceError,
    useOldPercentageRating,
    useGraphStatusV2,
    shouldShowFirstOnboarding,
    isAllRunnersOpen,
    onTooltipClick,
    setOnboardingStatus,
    numColumns,
    isNumberFireSection,
    bettingInterest,
    showRunnerUpdate,
    isWagerable,
    showWinProb,
    showPastRaces
  } = props;

  const findPastRacesLength = () => {
    const race = pastRaces && pastRaces.find((pastRace) => pastRace !== null);
    return !!race;
  };

  const renderPastRaces = (hasPastRaces: boolean) =>
    hasPastRaces &&
    showPastRaces && (
      <PastPerformances
        openPastPerformances={openPastPerformances}
        showIPP={showIPP}
        numberOfRaces={numberOfRaces}
        pastRaces={pastRaces}
      />
    );

  return useMemo(() => {
    const hasPastRaces = findPastRacesLength();
    return (
      <RunnerInfoContainer isLeg={isLeg}>
        <OddsDescription
          showBottomBorder={hasPastRaces}
          runner={runner}
          useGraphStatusV2={useGraphStatusV2}
          bettingInterest={bettingInterest}
          numColumns={numColumns}
          isNumberFireSection={isNumberFireSection}
          openHelpModal={openHelpModal}
          showRunnerUpdate={showRunnerUpdate}
          isWagerable={isWagerable}
          showWinProb={showWinProb}
        />
        {isLoadingPP && isEmpty(runnerStats) ? (
          <Fragment>
            {useGraphStatusV2 ? <RunnerInfoMaskV2 /> : <RunnerInfoMask />}
          </Fragment>
        ) : (
          <Fragment>
            {useGraphStatusV2 && get(runnerStats, "numberRaces") === 0 ? (
              <RunnerRatingTagContainer>
                <Tag
                  tagType="CoupledTag"
                  qaLabel="firstTimeRunning"
                  name="first time running"
                />
              </RunnerRatingTagContainer>
            ) : (
              <RunnerRating
                openHelpModal={openHelpModal}
                runner={runner}
                hasPastRaces={hasPastRaces}
                useOldPercentageRating={useOldPercentageRating}
                useGraphStatusV2={useGraphStatusV2}
                shouldShowFirstOnboarding={shouldShowFirstOnboarding}
                isAllRunnersOpen={isAllRunnersOpen}
                onTooltipClick={onTooltipClick}
                setOnboardingStatus={setOnboardingStatus}
              />
            )}
            {pastRaces !== null ? (
              renderPastRaces(hasPastRaces)
            ) : (
              <PastRacesError>
                {pastPerformanceError}
                <RetryButton onClick={() => refetch()} qaLabel="retry-button">
                  Retry
                </RetryButton>
              </PastRacesError>
            )}
          </Fragment>
        )}
      </RunnerInfoContainer>
    );
  }, [
    isLeg,
    isLoadingPP,
    showIPP,
    isAllRunnersOpen,
    JSON.stringify(runner),
    JSON.stringify(runnerStats),
    JSON.stringify(pastRaces),
    shouldShowFirstOnboarding,
    showWinProb,
    numColumns
  ]);
};

RunnerInfo.defaultProps = {
  runner: null,
  runnerStats: null,
  pastRaces: [],
  numberOfRaces: 0,
  isLoadingPP: true,
  isLeg: false,
  openHelpModal: noop,
  showIPP: true,
  openPastPerformances: noop,
  refetch: noop,
  pastPerformanceError: "",
  useOldPercentageRating: false,
  useGraphStatusV2: false,
  shouldShowFirstOnboarding: true,
  isAllRunnersOpen: false,
  onTooltipClick: noop,
  setOnboardingStatus: noop,
  numColumns: 1,
  isNumberFireSection: false,
  bettingInterest: noop,
  showRunnerUpdate: false,
  isWagerable: true,
  showWinProb: true,
  showPastRaces: true
};

export default RunnerInfo;
